const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export interface RaceOptionResult {
  optionId: string;
  optionText: string;
  voteCount: number;
  percentage: number;
}

export interface RaceResult {
  raceId: string;
  title: string;
  options: RaceOptionResult[];
}

export interface ElectionResults {
  electionId: string;
  races: RaceResult[];
  totalVotes: number;
  invalidVotes: number;
}

export interface ElectionResultsResponse {
  success: boolean;
  data: ElectionResults;
  metadata?: {
    electionTitle: string;
    electionStatus: string;
    publishedAt: string | null;
    isPublished?: boolean;
    isPreview?: boolean;
  };
}

/**
 * Results service for fetching and managing election results
 */
export class ResultsService {
  /**
   * Get election results (public if published)
   */
  static async getElectionResults(electionId: string): Promise<ElectionResultsResponse> {
    try {
      const response = await fetch(`${API_URL}/v1/results/${electionId}`);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch results');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching election results:', error);
      throw error;
    }
  }

  /**
   * Publish election results (admin only)
   */
  static async publishResults(
    electionId: string,
    accessToken: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(`${API_URL}/v1/results/${electionId}/publish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to publish results');
      }

      return await response.json();
    } catch (error) {
      console.error('Error publishing results:', error);
      throw error;
    }
  }

  /**
   * Preview election results before publication (admin only)
   */
  static async previewResults(
    electionId: string,
    accessToken: string
  ): Promise<ElectionResultsResponse> {
    try {
      const response = await fetch(`${API_URL}/v1/results/${electionId}/preview`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to preview results');
      }

      return await response.json();
    } catch (error) {
      console.error('Error previewing results:', error);
      throw error;
    }
  }
}
